import {
    CategoryType,
    CollectionType,
    GameType,
    CategoriesAndGamesType,
    ProviderZod,
} from 'src/domains/casino/types/types';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { Common } from 'src/domains/common/Common';
import { CollectionClientType, CustomerClientType } from './adapter';
import {
    openapiCmsAnonymousGetCategoriesAndGamesRequest,
    OpenapiCmsAnonymousGetCategoriesAndGamesResponse200Type,
} from 'src/domains/casino/api/generated/cms/openapi_cms_anonymous_get_categories_and_games';
import {
    openapiCmsCustomerGetRecentGamesRequest,
    OpenapiCmsCustomerGetRecentGamesResponse200Type,
} from 'src/domains/casino/api/generated/cms/openapi_cms_customer_get_recent_games';
import {
    openapiCmsCustomerGetFavouriteGamesRequest,
    OpenapiCmsCustomerGetFavouriteGamesResponse200Type,
} from 'src/domains/casino/api/generated/cms/openapi_cms_customer_get_favourite_games';
import { openapiCmsCustomerSetFavouriteGameRequest } from 'src/domains/casino/api/generated/cms/openapi_cms_customer_set_favourite_game';
import { openapiCmsCustomerSetRecentGameRequest } from 'src/domains/casino/api/generated/cms/openapi_cms_customer_set_recent_game';
import { slugifyString } from 'src/domains/casino/utils/utils';
import { runAsync } from 'src_common/common/mobx-utils/runAsync';
import { action, autorun, computed, makeObservable, observable } from 'mobx';

export class CmsCollectionClient implements CollectionClientType {
    private categoriesAndGamesResource: Resource<OpenapiCmsAnonymousGetCategoriesAndGamesResponse200Type>;
    @observable.ref public categoriesAndGames: CategoriesAndGamesType = {
        categories: [],
        games: [],
    };

    public constructor(
        private readonly collection: CollectionType,
        private readonly common: Common
    ) {
        makeObservable(this);
        this.categoriesAndGamesResource = new Resource(
            async (): Promise<OpenapiCmsAnonymousGetCategoriesAndGamesResponse200Type> => {
                return this.common.session.call(openapiCmsAnonymousGetCategoriesAndGamesRequest, {
                    collection_slug: this.collection,
                });
            }
        );
        autorun((reaction) => {
            const result = this.categoriesAndGamesResource.get();
            if (result.type === 'ready') {
                const dataRaw = result.value;
                const categories: Array<CategoryType> = dataRaw.categories.map((category) => ({
                    id: category.id,
                    name: category.name,
                    active: category.active,
                    fixed: category.fixed,
                    visibleInNavigation: category.visible_in_navigation,
                    visibleInAllGames: category.visible_in_all_games,
                    gameIds: category.game_ids,
                }));
                const games: Array<GameType> = dataRaw.games.map((game) => ({
                    id: game.id,
                    providerGameId: game.launch_game_id,
                    name: game.name,
                    demoMode: game.demo,
                    providerId: NaN,
                    // game.provider in cms has format 'Evolution', 'SG Digital' ect.
                    providerSlug: ProviderZod.parse(slugifyString(game.provider)),
                    providerName: game.provider,
                    studioId: NaN,
                    studioSlug: game.studio_id,
                    studioName: game.studio_name,
                    published: game.published,
                    imageHorizontal: game.image,
                    imageVertical: game.image_vertical,
                    imageSquare: game.image_square,
                    description: game.description,
                    jackpotAmount: game.jackpot_amount,
                    returnToPlayer: game.return_to_player,
                    minBet: game.min_bet,
                    maxBet: game.max_bet,
                    maxWin: game.max_win,
                }));
                this.setCategoriesAndGames(categories, games);
                reaction.dispose();
            }
        });
    }

    @action private setCategoriesAndGames(categories: Array<CategoryType>, games: Array<GameType>): void {
        this.categoriesAndGames = { categories, games };
    }

    @computed public get isLoading(): boolean {
        const result = this.categoriesAndGamesResource.get();
        return result.type === 'loading';
    }
}

export class CmsCustomerClient implements CustomerClientType {
    private recentGamesIdsResource: Resource<OpenapiCmsCustomerGetRecentGamesResponse200Type>;
    private favouriteGamesIdsResource: Resource<OpenapiCmsCustomerGetFavouriteGamesResponse200Type>;

    public constructor(private readonly common: Common) {
        this.recentGamesIdsResource = new Resource(
            async (): Promise<OpenapiCmsCustomerGetRecentGamesResponse200Type> => {
                return this.common.session.call(openapiCmsCustomerGetRecentGamesRequest, {});
            }
        );
        this.favouriteGamesIdsResource = new Resource(
            async (): Promise<OpenapiCmsCustomerGetFavouriteGamesResponse200Type> => {
                return this.common.session.call(openapiCmsCustomerGetFavouriteGamesRequest, {});
            }
        );
    }

    public get recentGamesIds(): OpenapiCmsCustomerGetRecentGamesResponse200Type['game_ids'] {
        const result = this.recentGamesIdsResource.get();
        if (result.type === 'ready') {
            return result.value.game_ids;
        }
        return [];
    }

    public get isLoadingRecent(): boolean {
        const result = this.recentGamesIdsResource.get();
        return result.type === 'loading';
    }

    public setRecentGame = (gameId: number): void => {
        runAsync(async () => {
            await this.common.session.call(openapiCmsCustomerSetRecentGameRequest, {
                requestBody: {
                    game_id: gameId,
                },
            });
            await this.recentGamesIdsResource.refresh();
        });
    };

    public get favouriteGamesIds(): OpenapiCmsCustomerGetFavouriteGamesResponse200Type['game_ids'] {
        const result = this.favouriteGamesIdsResource.get();
        if (result.type === 'ready') {
            return result.value.game_ids;
        }
        return [];
    }

    public get isLoadingFavourites(): boolean {
        const result = this.favouriteGamesIdsResource.get();
        return result.type === 'loading';
    }

    public setFavouriteGame = (gameId: number, isFavourite: boolean): void => {
        runAsync(async () => {
            await this.common.session.call(openapiCmsCustomerSetFavouriteGameRequest, {
                requestBody: {
                    game_id: gameId,
                    is_favourite: isFavourite,
                },
            });
            await this.favouriteGamesIdsResource.refresh();
        });
    };
}
